(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabPreviewLaunch = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  function derivePreviewLaunchState(petCount, uploadStatuses, photoFlags, photoChecking) {
    var count = Number(petCount) || 0;
    var statuses = Array.isArray(uploadStatuses) ? uploadStatuses.slice(0, count) : [];
    var flags = Array.isArray(photoFlags) ? photoFlags.slice(0, count) : [];
    // A red photo-quality flag blocks the customizer until the photo is replaced.
    var photoBlocked = flags.some(function (flag) {
      return flag === 'red';
    });
    // A photo still being checked holds the customizer too (for a short while,
    // see PjPhotoQuality.holdsPreview), so the Lab never opens on a photo that
    // is about to come back red.
    var checking = Boolean(photoChecking) && !photoBlocked;

    return {
      petCount: count,
      // No resolved pet slots means the shopper has not picked a pet count yet,
      // so there is nothing to preview and the control stays out of the buy box.
      visible: count > 0,
      enabled:
        count > 0 &&
        !photoBlocked &&
        !checking &&
        statuses.length === count &&
        statuses.every(function (status) {
          return status === 'done';
        }),
      photoBlocked: photoBlocked,
      photoChecking: checking,
    };
  }

  /**
   * The uploader is not always a sibling of the wrap: on a multi-pet PDP it is
   * rendered inside the pet-count variant fieldset. Look outwards from the wrap
   * and fall back to the document so the control works from wherever the theme
   * renders it.
   */
  function findUploader(wrap, className) {
    var sibling = wrap.previousElementSibling;
    while (sibling) {
      if (sibling.classList && sibling.classList.contains(className)) return sibling;
      var nested = sibling.querySelector && sibling.querySelector('.' + className);
      if (nested) return nested;
      sibling = sibling.previousElementSibling;
    }

    var scope = wrap.closest('form') || (root.document && root.document.body);
    return scope ? scope.querySelector('.' + className) : null;
  }

  function visibleMultiSlots(wrap) {
    var uploader = findUploader(wrap, 'multi-image-uploader');
    if (!uploader) return [];

    return Array.prototype.filter
      .call(uploader.querySelectorAll('.uploaderSection'), function (section) {
        return (
          section.style.display !== 'none' &&
          Boolean(section.querySelector('input:not([disabled])'))
        );
      })
      .map(function (section) {
        return section.querySelector('.photo-upload-slot');
      });
  }

  function singleSlots(wrap) {
    var uploader = findUploader(wrap, 'single-image-uploader');
    var slot = uploader && uploader.querySelector('.photo-upload-slot');
    return slot ? [slot] : [];
  }

  // `data-uploader-kind` is a preference, not a guarantee: a product can carry
  // both uploader tags, so whichever uploader actually rendered slots wins.
  function uploaderSlots(wrap) {
    var preferMulti = wrap.dataset.uploaderKind === 'multi';
    var primary = preferMulti ? visibleMultiSlots(wrap) : singleSlots(wrap);
    if (primary.length) return primary;

    return preferMulti ? singleSlots(wrap) : visibleMultiSlots(wrap);
  }

  /**
   * Prefer the status the uploader stamps on the slot, but fall back to the
   * rendered zone so a slot drawn before this script loaded still reads as done.
   */
  function slotStatus(slot) {
    if (!slot) return 'idle';
    if (slot.dataset.uploadStatus) return slot.dataset.uploadStatus;
    if (slot.querySelector('.photo-upload-zone.has-photo')) return 'done';
    if (slot.querySelector('.photo-upload-zone.is-loading')) return 'loading';
    return 'idle';
  }

  function slotPhotoFlag(slot) {
    return (slot && slot.dataset && slot.dataset.photoFlag) || '';
  }

  function slotImageUrl(slot) {
    if (!slot) return '';
    var image = slot.querySelector('.photo-preview-img');
    return image ? image.currentSrc || image.src || image.getAttribute('src') || '' : '';
  }

  function selectedBackground(wrap) {
    var scope = wrap.closest('form') || (root.document && root.document.body);
    // Empty rather than a guessed default: the renderer decides whether an
    // absent color means "not chosen yet" or "product has no color axis".
    if (!scope || typeof scope.querySelector !== 'function') return '';

    var selected =
      scope.querySelector('input[name*="-Color-"]:checked') ||
      scope.querySelector('input[name*="-Background-"]:checked') ||
      scope.querySelector('input[name*="-Colour-"]:checked') ||
      scope.querySelector('input[name*="Color"]:checked');
    if (selected && selected.value) return String(selected.value).trim();

    var background = scope.querySelector('.backgroundHiddenInput:not(.backgroundHiddenInputBandana)');
    return background && background.value ? String(background.value).trim() : '';
  }

  function previewContext(wrap) {
    var slots = uploaderSlots(wrap);
    var state = derivePreviewLaunchState(slots.length, slots.map(slotStatus));
    return {
      petCount: state.petCount,
      petImages: slots.map(slotImageUrl),
      color: selectedBackground(wrap),
    };
  }

  function hasCompletePetImages(context) {
    return (
      context.petCount > 0 &&
      context.petImages.length === context.petCount &&
      context.petImages.every(function (imageUrl) {
        return Boolean(imageUrl);
      })
    );
  }

  // The modal delegates replacement to the PDP file input instead of running a
  // second upload implementation. That keeps the uploader card, hidden cart
  // property, upload validation, and Design Lab refresh on one code path.
  function replacePetPhoto(petIndex, files) {
    if (!root.document || !files || !files.length) return false;

    var index = Number(petIndex);
    if (!Number.isInteger(index) || index < 0) return false;

    var wraps = root.document.querySelectorAll('.preview-launch-wrap');
    for (var wrapIndex = 0; wrapIndex < wraps.length; wrapIndex += 1) {
      var wrap = wraps[wrapIndex];
      if (wrap.hidden) continue;

      var slot = uploaderSlots(wrap)[index];
      var input = slot && slot.querySelector('.fileInput');
      if (!input || typeof input.dispatchEvent !== 'function') continue;

      try {
        input.files = files;
      } catch (error) {
        return false;
      }

      var changeEvent =
        typeof root.Event === 'function'
          ? new root.Event('change', { bubbles: true })
          : { type: 'change', bubbles: true };
      if (root.PjAnalytics) root.PjAnalytics.markReplaceUpload(index);
      input.dispatchEvent(changeEvent);
      return true;
    }

    return false;
  }

  // Pets the PDP is selling right now through a visible Design Lab control: 0 when the
  // control is hidden (no pet count) or absent (a color or product without Design Lab),
  // null outside a browser. Add to Cart uses it to drop a design the PDP no longer matches.
  function activePetCount() {
    if (!root.document) return null;
    var wraps = root.document.querySelectorAll('.preview-launch-wrap');
    if (!wraps.length) return 0;
    for (var i = 0; i < wraps.length; i += 1) {
      if (!wraps[i].hidden) return uploaderSlots(wraps[i]).length;
    }
    return 0;
  }

  var staleCountCheck = null;

  // Zero slots is a buy-box swap in flight (photos are restored right after), not a
  // shopper picking fewer pets.
  function scheduleStaleCountCheck() {
    if (staleCountCheck || !root.setTimeout) return;
    staleCountCheck = root.setTimeout(function () {
      staleCountCheck = null;
      var controls = root.PjDesignLabControls;
      var saved = savedPetCount();
      var live = root.document ? activePetCount() : null;
      if (saved > 0 && live > 0 && live !== saved && controls.invalidateFinalized) {
        controls.invalidateFinalized();
      }
    }, 0);
  }

  function savedPetCount() {
    var controls = root.PjDesignLabControls;
    var saved = controls && controls.finalizedDesign && controls.finalizedDesign();
    return saved ? Number(saved.petCount) || 0 : 0;
  }

  function isDesignSaved() {
    var controls = root.PjDesignLabControls;
    return Boolean(controls && controls.finalizedDesign && controls.finalizedDesign());
  }

  function updateWrap(wrap) {
    var button = wrap.querySelector('#previewLaunchBtn');
    var label = wrap.querySelector('[data-preview-launch-label]');
    var hint = wrap.querySelector('[data-preview-launch-hint]');
    var disclaimer = wrap.querySelector('[data-preview-launch-disclaimer]');
    if (!button || !label || !hint) return;

    var slots = uploaderSlots(wrap);
    var quality = root.PjPhotoQuality;
    var state = derivePreviewLaunchState(
      slots.length,
      slots.map(slotStatus),
      slots.map(slotPhotoFlag),
      Boolean(quality && typeof quality.holdsPreview === 'function' && quality.holdsPreview())
    );
    var saved = isDesignSaved();
    var baseLabel = saved
      ? wrap.dataset.editButtonLabel || 'Edit Your Design'
      : wrap.dataset.buttonLabel || 'Preview Your Design';
    Array.prototype.forEach.call(
      wrap.querySelectorAll('[data-preview-launch-icon]'),
      function (icon) {
        // SVG elements have no .hidden property, so toggle the attribute itself.
        var hideIcon = (icon.getAttribute('data-preview-launch-icon') === 'edit') !== saved;
        if (hideIcon) icon.setAttribute('hidden', '');
        else icon.removeAttribute('hidden');
      }
    );

    wrap.hidden = !state.visible;

    label.textContent =
      state.petCount > 1 ? baseLabel + ' (' + state.petCount + ' pets)' : baseLabel;
    // A red photo leaves the button focusable and clickable (aria-disabled
    // only), so the attempt can be turned away with a reason and recorded.
    button.disabled = !state.enabled && !state.photoBlocked;
    button.setAttribute('aria-disabled', String(!state.enabled));
    if (button.classList) button.classList.toggle('is-photo-blocked', state.photoBlocked);
    hint.hidden = state.enabled;
    if (hint.classList) hint.classList.toggle('preview-launch-hint--red', state.photoBlocked);
    hint.textContent = state.photoBlocked
      ? (quality && quality.previewHint && quality.previewHint()) ||
        'Upload a new photo to preview your design.'
      : state.photoChecking
        ? (quality && quality.readConfig && quality.readConfig().checkingText) ||
          'Checking your photo…'
      : state.petCount > 1
        ? wrap.dataset.multiPetHint || 'Upload all photos to preview your design.'
        : wrap.dataset.singlePetHint || 'Upload a photo to preview your design.';
    if (disclaimer) disclaimer.hidden = !state.enabled;
    trackLaunchExposure(state);
  }

  // updateWrap runs on every refresh, so exposure is once per page view.
  function trackLaunchExposure(state) {
    var analytics = root.PjAnalytics;
    if (!analytics) return;
    if (state.visible) analytics.trackOnce(analytics.EVENTS.launchShown, 'shown');
    if (state.visible && state.enabled) {
      analytics.trackOnce(analytics.EVENTS.launchEnabled, 'enabled');
    }
  }

  function openPreview(context) {
    if (typeof root.openPreview === 'function') {
      root.openPreview(context);
      return;
    }

    var modal = root.document && root.document.getElementById('pvModal');
    if (modal) modal.classList.add('open');
  }

  function wireWrap(wrap) {
    var button = wrap.querySelector('#previewLaunchBtn');
    if (!button || button.dataset.previewLaunchWired === 'true') return;

    button.dataset.previewLaunchWired = 'true';
    button.addEventListener('click', function () {
      if (button.disabled) return;
      if (button.getAttribute('aria-disabled') === 'true') {
        var quality = root.PjPhotoQuality;
        if (quality && typeof quality.blockIfRed === 'function') quality.blockIfRed('preview');
        return;
      }
      if (root.PjAnalytics) {
        root.PjAnalytics.track(root.PjAnalytics.EVENTS.labOpened, {
          entry: isDesignSaved() ? 'edit' : 'launch',
        });
      }
      openPreview(previewContext(wrap));
    });
    updateWrap(wrap);
  }

  function refresh() {
    if (!root.document) return;
    var wraps = root.document.querySelectorAll('.preview-launch-wrap');
    Array.prototype.forEach.call(wraps, wireWrap);
    Array.prototype.forEach.call(wraps, updateWrap);

    // A PDP change made while the studio is closed (photo, color, pet count) still
    // has to un-save the design, so reconcile the store before labels are drawn.
    var controlsForSaved = root.PjDesignLabControls;
    if (isDesignSaved() && controlsForSaved.setContext) {
      for (var w = 0; w < wraps.length; w += 1) {
        if (wraps[w].hidden) continue;
        var savedContext = previewContext(wraps[w]);
        // A new slot has no photo yet, so setContext is skipped; the pet count alone makes
        // the saved design stale. Slots are rebuilt one at a time on a pet-count or color change, so a count read
        // mid-rebuild is transient. Judge the settled count on the next tick, never this one.
        if (savedPetCount() > 0 && savedContext.petCount !== savedPetCount()) {
          scheduleStaleCountCheck();
        } else if (hasCompletePetImages(savedContext)) {
          controlsForSaved.setContext(savedContext);
        }
        break;
      }
      Array.prototype.forEach.call(wraps, updateWrap);
    }

    var modal =
      typeof root.document.getElementById === 'function'
        ? root.document.getElementById('pvModal')
        : null;
    var controls = root.PjDesignLabControls;
    if (modal && modal.classList.contains('open') && controls) {
      for (var index = 0; index < wraps.length; index += 1) {
        if (!wraps[index].hidden) {
          var context = previewContext(wraps[index]);
          // During replacement the PDP slot intentionally has no image while
          // its upload UI is loading. Keep the old studio state until a new URL
          // commits, then prepare() clears stale processing and reprocesses only
          // the changed pet.
          if (!hasCompletePetImages(context)) break;
          if (typeof controls.prepare === 'function') controls.prepare(context);
          else if (typeof controls.setContext === 'function') controls.setContext(context);
          break;
        }
      }
    }
  }

  function init() {
    if (!root.document) return;
    refresh();
    root.document.addEventListener('pj-design-lab:uploader-state-change', refresh);
    root.document.addEventListener('variant:change', refresh);
    root.document.addEventListener('pj-design-lab:photo-flag-change', refresh);
    root.addEventListener('pj-design-lab:finalized-change', function () {
      Array.prototype.forEach.call(
        root.document.querySelectorAll('.preview-launch-wrap'),
        updateWrap
      );
    });
  }

  if (root.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }

  return {
    activePetCount: activePetCount,
    derivePreviewLaunchState: derivePreviewLaunchState,
    hasCompletePetImages: hasCompletePetImages,
    previewContext: previewContext,
    replacePetPhoto: replacePetPhoto,
    refresh: refresh,
  };
});
